import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  contactErrorMessage,
  markNextEventContactHandled,
  subscribeToNextEventContacts,
  type NextEventContact,
} from '../../services/nextEventContact';
import {
  CROWD_SIZE_LABELS,
  DJ_EVENTS_PER_YEAR_LABELS,
  EVENT_TIMING_LABELS,
  EVENT_TYPE_LABELS,
  PREFERRED_CONTACT_LABELS,
  ROLE_SHORT_LABELS,
  SOURCE_LABELS,
} from '../../utils/nextEventContact';

const sectionClass = 'bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4';
const headingClass = 'text-lg font-semibold text-gray-900 dark:text-white';
const tableClass = 'w-full text-sm text-left text-gray-700 dark:text-gray-300';
const cellClass = 'py-2 pr-4 align-top';

function formatWhen(date: Date | null): string {
  return date ? date.toLocaleString() : '—';
}

function whoText(contact: NextEventContact): string {
  const parts = [
    contact.role ? ROLE_SHORT_LABELS[contact.role] : 'No answer',
    contact.djEventsPerYear ? `${DJ_EVENTS_PER_YEAR_LABELS[contact.djEventsPerYear]} events/yr` : null,
    contact.eventType ? EVENT_TYPE_LABELS[contact.eventType] : null,
    contact.eventTiming ? EVENT_TIMING_LABELS[contact.eventTiming] : null,
    contact.crowdSize ? `${CROWD_SIZE_LABELS[contact.crowdSize]} people` : null,
  ];
  return parts.filter(Boolean).join(' · ');
}

// Leads from the "VoteBeats at your next event?" form (ITER-200); only owner admins can read them
export function NextEventContactsSection() {
  const [contacts, setContacts] = useState<NextEventContact[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    return subscribeToNextEventContacts(
      (next) => {
        setContacts(next);
        setLoadError(null);
      },
      (error) => {
        console.error('Could not load next-event contacts', error);
        setLoadError('Could not load the contact requests.');
      },
    );
  }, []);

  const handleSetHandled = async (contactId: string, handled: boolean) => {
    setBusyId(contactId);
    try {
      await markNextEventContactHandled(contactId, handled);
    } catch (error) {
      console.error('Could not update the contact request', error);
      toast.error(contactErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section id="next-event-contacts" className={sectionClass} data-testid="next-event-contacts-section">
      <h2 className={headingClass}>Next-event contacts</h2>
      {loadError && (
        <div role="alert" className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {loadError}
        </div>
      )}
      {!loadError && contacts.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No contact requests yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={cellClass}>When</th>
                <th className={cellClass}>Name</th>
                <th className={cellClass}>Contact</th>
                <th className={cellClass}>Who</th>
                <th className={cellClass}>Note</th>
                <th className={cellClass}>Event</th>
                <th className={cellClass}>From</th>
                <th className={cellClass}>Email</th>
                <th className={cellClass}>Handled</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr
                  key={contact.id}
                  className={`border-t border-gray-100 dark:border-gray-700 ${contact.handledAt ? 'opacity-50' : ''}`}
                  data-testid={`contact-row-${contact.id}`}
                >
                  <td className={cellClass}>{formatWhen(contact.createdAt)}</td>
                  <td className={cellClass}>{contact.name}</td>
                  <td className={cellClass}>
                    <div className="space-y-0.5">
                      {contact.email && <a href={`mailto:${contact.email}`} className="block text-primary hover:underline">{contact.email}</a>}
                      {contact.phone && <a href={`tel:${contact.phone}`} className="block text-primary hover:underline">{contact.phone}</a>}
                      {contact.preferredContact && (
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          prefers {PREFERRED_CONTACT_LABELS[contact.preferredContact].toLowerCase()}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={cellClass}>{whoText(contact)}</td>
                  <td className={`${cellClass} whitespace-pre-wrap max-w-xs`}>{contact.note ?? '—'}</td>
                  <td className={cellClass}>
                    {contact.eventName && contact.eventUrl ? (
                      <a href={`/e/${contact.eventUrl}`} className="text-primary hover:underline">{contact.eventName}</a>
                    ) : (
                      contact.eventName ?? '—'
                    )}
                  </td>
                  <td className={cellClass}>{SOURCE_LABELS[contact.source] ?? contact.source}</td>
                  <td className={cellClass}>
                    <span className={contact.emailStatus === 'held' || contact.emailStatus === 'failed' ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
                      {contact.emailStatus}
                    </span>
                  </td>
                  <td className={cellClass}>
                    {contact.handledAt ? (
                      <span className="whitespace-nowrap">
                        Handled {contact.handledAt.toLocaleDateString()} ·{' '}
                        <button
                          type="button"
                          onClick={() => handleSetHandled(contact.id, false)}
                          disabled={busyId === contact.id}
                          className="text-primary hover:underline disabled:opacity-50"
                        >
                          Undo
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleSetHandled(contact.id, true)}
                        disabled={busyId === contact.id}
                        className="btn btn-secondary text-xs px-2 py-1 disabled:opacity-50"
                      >
                        Mark handled
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
